import React, { useEffect, useState, memo } from 'react';
import { Tabs, Typography, Row, Col, Input } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { getID } from 'src/services';
import { successMessage, errorMessage } from 'src/utils/Notifications';
import { faMinusSquare } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MessagesCard, PrimaryButton } from '../components';
import { IMessages } from '../interfaces';
import { GET_CHANNELS, selectedMessage, GET_MESSAGES, SEND_MESSAGE, SET_MESSAGE_AS_READ } from '../graphql';
import { ARCHIVED, SENT } from '../services';
import Paginate from '../components/Paginate';

import userIcon from '../assets/images/userIcon.svg';
import MessageList from '../components/MessageList';
// import { selectedMessage } from '../graphql/variables';

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { TextArea } = Input;

function MessageNotifications(): React.ReactElement {
    const { msgId } = useParams();
    const selectedMessageVar: string = useReactiveVar(selectedMessage);
    const [messages, setMessages] = useState<IMessages[]>([]);
    const [message, setMessage] = useState<IMessages>();
    const [layout, setLayout] = useState<string>('inbox');
    const [messagesArchive, setMessagesArchive] = useState<IMessages[]>([]);
    const [getMessages, { data: getMessagesData }] = useLazyQuery(GET_MESSAGES, {
        variables: { status: 'SENT', otherUserId: message?.to?.id },
    });
    const [sendMessage] = useMutation(SEND_MESSAGE, {
        refetchQueries: [GET_MESSAGES, 'getMessages'],
    });

    const [setMessageAsRead] = useMutation(SET_MESSAGE_AS_READ);

    const [offset, setOffset] = useState(0);
    const [count, setCount] = useState(20);
    const [total, setTotal] = useState(0);

    const [newMesssage, setNewMesssage] = useState('');
    const {
        loading: inboxLoading,
        data: inboxData,
        refetch: refetchInbox,
        startPolling,
        stopPolling,
    } = useQuery(GET_CHANNELS, {
        variables: { count, offset, status: SENT },
        fetchPolicy: 'network-only',
    });
    const {
        loading: archiveLoading,
        data: archiveData,
        refetch: refetchArchive,
        startPolling: startPollingArchive,
        stopPolling: stopPollingArchive,
    } = useQuery(GET_CHANNELS, {
        variables: { count, offset, status: ARCHIVED },
        fetchPolicy: 'network-only',
    });

    const getPartyId = (msg: IMessages) => {
        const currentUser = getID();
        return msg?.from?.id === currentUser ? msg?.to?.id : msg?.from?.id;
    };

    const handleGetMessage = (messageId: string) => {
        const msg = messages.find((m) => m.id === messageId);
        setMessage(msg);
        if (msg !== undefined) {
            // get the counterpart ID
            const userId = getPartyId(msg);
            setMessageAsRead({
                variables: {
                    fromId: userId,
                },
            })
                .then(() => {})
                .catch((err) => {
                    console.log(err);
                });
        }
    };

    const handleGetMessageArchive = (messageId: string) => {
        const msg = messagesArchive.find((m) => m.id === messageId);
        setMessage(msg);
    };

    const checkSenderIfSelf = (msg: IMessages) => {
        const currentUser = getID();
        if (msg?.from?.id === currentUser) {
            return true;
        }
        return false;
    };

    const getParty = (msg: IMessages) => {
        const currentUser = getID();
        return msg?.from?.id === currentUser
            ? `${msg?.to?.firstName} ${msg?.to?.lastName}`
            : `${msg?.from?.firstName} ${msg?.from?.lastName}`;
    };

    const handleMinimize = () => {
        selectedMessage('');
    };

    const handleSwitch = (messageLayout: string) => {
        setLayout(messageLayout);
    };

    const handleSubmitMessage = () => {
        if (message === undefined) {
            errorMessage("No recipient found, click on 'Compose' instead");
            return;
        }
        if (message.status === 'ARCHIVED') {
            errorMessage('You cannot send new message on this thread');
            return;
        }
        const userId = getPartyId(message);
        sendMessage({
            variables: {
                text: newMesssage,
                to: userId,
            },
        })
            .then(() => {
                // eslint-disable-next-line no-void
                try {
                    setNewMesssage('');
                    refetchInbox()
                        .then(() => {})
                        .catch(() => {});
                    // eslint-disable-next-line no-void
                    // void getMessages();
                } catch (error) {
                    console.log('while error in getting', getMessages);
                }
                successMessage('Message sent successfully');
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const handleNextArchive = (from: number) => {
        setOffset(from);
        refetchArchive()
            .then(() => {})
            .catch(() => {});
    };

    const handleNextInbox = (from: number) => {
        setOffset(from);
        refetchInbox()
            .then(() => {})
            .catch(() => {});
    };

    useEffect(() => {
        if (selectedMessageVar === '') {
            startPolling(5000);
            startPollingArchive(5000);
        } else {
            stopPolling();
            stopPollingArchive();
        }
        return () => {
            stopPolling();
            stopPollingArchive();
        };
    }, [selectedMessageVar]);

    useEffect(() => {
        if (msgId !== null && msgId !== undefined) {
            handleGetMessage(msgId);
        }
        if (inboxData?.getChannels) {
            setMessages(inboxData.getChannels.messages);
            const msg = inboxData.getChannels.messages.length > 0 ? inboxData.getChannels.messages[0] : undefined;
            setMessage(msg);
            setTotal(inboxData.getChannels?.pagination?.total);
        }
    }, [inboxData]);

    useEffect(() => {
        if (archiveData?.getChannels) {
            setMessagesArchive(archiveData.getChannels.messages);
            const msg = archiveData.getChannels.messages.length > 0 ? archiveData.getChannels.messages[0] : undefined;
            setTotal(archiveData.getChannels?.pagination?.total);
        }
    }, [archiveData]);

    return (
        <>
            <Row
                className="space-between min-width-mobile-320"
                style={{ paddingBottom: '0px', background: '#ffffff', borderRadius: '15px' }}
            >
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6 }} lg={{ span: 6 }}>
                    <div className="card-container" style={{ width: '100%' }}>
                        <Row style={{ padding: '5px', marginTop: '5px' }}>
                            <Col span="12" style={{ textAlign: 'center' }}>
                                <Text
                                    style={{ fontWeight: 'bold', color: '#0b543e' }}
                                    onClick={() => {
                                        handleSwitch('inbox');
                                    }}
                                >
                                    Inbox
                                </Text>
                            </Col>
                            <Col span="12" style={{ textAlign: 'center' }}>
                                <Text
                                    style={{ fontWeight: 'bold', color: '#0b543e' }}
                                    onClick={() => {
                                        handleSwitch('deleted');
                                    }}
                                >
                                    Deleted
                                </Text>
                            </Col>
                        </Row>

                        <Row style={{ padding: '5px' }}>
                            <Col span="24">
                                {layout === 'inbox' ? (
                                    <MessagesCard
                                        withoutHeader
                                        inbox
                                        fetchSelectedMessage={(messageID: string) => {
                                            selectedMessage(messageID);
                                            handleGetMessage(messageID);
                                        }}
                                        messages={messages}
                                        loading={inboxLoading}
                                        refetch={refetchInbox}
                                        page="message"
                                    />
                                ) : (
                                    <MessagesCard
                                        withoutHeader
                                        deleted
                                        fetchSelectedMessage={(messageID: string) => {
                                            selectedMessage(messageID);
                                            handleGetMessageArchive(messageID);
                                        }}
                                        messages={messagesArchive}
                                        loading={archiveLoading}
                                        refetch={refetchArchive}
                                        page="message"
                                    />
                                )}
                            </Col>
                            <Col span="24" style={{ marginTop: '4px' }}>
                                {layout === 'deleted' && messagesArchive.length > 0 && (
                                    <Paginate pageCount={total / count} getRecords={handleNextArchive} />
                                )}
                                {layout === 'inbox' && messages.length > 0 && (
                                    <Paginate pageCount={total / count} getRecords={handleNextInbox} />
                                )}
                            </Col>
                        </Row>
                    </div>
                </Col>
                <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 18 }}
                    lg={{ span: 18 }}
                    style={{ borderLeft: '1px solid #C9E0C9' }}
                >
                    <Row>
                        <Col
                            xs={{ span: 24 }}
                            sm={{ span: 18 }}
                            md={{ span: 12 }}
                            lg={{ span: 12 }}
                            style={{ padding: '7px' }}
                        >
                            {message !== undefined && (
                                <Row
                                    className="selectable ant-layout-flex-left"
                                    style={{ width: '100%', display: 'flex' }}
                                >
                                    <Col>
                                        <img
                                            src={userIcon}
                                            alt=""
                                            className="large-icon"
                                            style={{ width: '40px', height: '40px' }}
                                        />
                                    </Col>
                                    <Col style={{ padding: '10px', paddingTop: '15px' }}>
                                        <Title style={{ fontSize: '15px', color: '#0b543e' }}>
                                            {message !== undefined ? getParty(message) : ''}
                                        </Title>
                                    </Col>
                                </Row>
                            )}
                        </Col>
                        <Col
                            xs={{ span: 24 }}
                            sm={{ span: 8 }}
                            md={{ span: 12 }}
                            lg={{ span: 12 }}
                            style={{ textAlign: 'right' }}
                        >
                            <Link to="compose-message">
                                <PrimaryButton
                                    title="Compose"
                                    htmlType="submit"
                                    styles={{
                                        backgroundColor: '#EE773B',
                                        borderRadius: '20px',
                                        textAlign: 'center',
                                        lineHeight: '25px',
                                        fontSize: '16px',
                                        height: '32px',
                                        width: '100px',
                                        padding: '0px',
                                        marginRight: '10px',
                                        marginTop: '10px',
                                        marginBottom: '10px',
                                    }}
                                />
                            </Link>
                        </Col>
                    </Row>

                    <Row style={{ borderTop: '1px solid #C9E0C9' }}>
                        <Col span="24" style={{ minHeight: '300px', overflowY: 'auto' }}>
                            {message !== undefined && (
                                <>
                                    <Row style={{ maxHeight: '80vh', position: 'relative' }}>
                                        {checkSenderIfSelf(message) ? (
                                            <MessageList
                                                channelId={message?.id}
                                                othersId={message?.to.id}
                                                othersUserPhoto={message?.to.photo}
                                                othersFirstName={message?.to.firstName}
                                                othersLastname={message?.to.lastName}
                                                messageType={layout === 'inbox' ? 'SENT' : 'ARCHIVED'}
                                            />
                                        ) : (
                                            <MessageList
                                                channelId={message?.id}
                                                othersId={message?.from.id}
                                                othersUserPhoto={message?.from.photo}
                                                othersFirstName={message?.to.firstName}
                                                othersLastname={message?.to.lastName}
                                                messageType={layout === 'inbox' ? 'SENT' : 'ARCHIVED'}
                                            />
                                        )}
                                    </Row>
                                </>
                            )}
                        </Col>
                        <Col span="24" style={{ borderTop: '1px solid #C9E0C9' }}>
                            <Row>
                                <Col xs={{ span: 24 }} sm={{ span: 18 }} md={{ span: 20 }} lg={{ span: 20 }}>
                                    <TextArea
                                        rows={1}
                                        placeholder="Write a message"
                                        maxLength={2000}
                                        value={newMesssage}
                                        onChange={(e) => setNewMesssage(e.target.value)}
                                        style={{ border: 0 }}
                                    />
                                </Col>
                                <Col
                                    xs={{ span: 24 }}
                                    sm={{ span: 6 }}
                                    md={{ span: 4 }}
                                    lg={{ span: 4 }}
                                    style={{ textAlign: 'right', paddingRight: '2px' }}
                                >
                                    <PrimaryButton
                                        title="Send"
                                        htmlType="submit"
                                        styles={{
                                            backgroundColor: '#EE773B',
                                            borderRadius: '20px',
                                            textAlign: 'center',
                                            lineHeight: '25px',
                                            fontSize: '16px',
                                            height: '32px',
                                            width: '100px',
                                            padding: '0px',
                                            marginLeft: '10px',
                                            marginTop: '10px',
                                            marginBottom: '10px',
                                        }}
                                        onClick={() => {
                                            handleSubmitMessage();
                                        }}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </>
    );
}

export default memo(MessageNotifications);
